import { set } from '@shopworx/services/util/store.helper';
import appConfig from './config/appConfig';
import {
  BILLING_ELEMENTS,
  BILLING_ELEMENT_NAMES,
  getTagSyncPayload,
} from './onboarding/billingElements';
import { BillingAppConfigService, CostCategoriesService } from './services';

export default ({
  namespaced: true,

  state: {
    drawer: true,
    masterElements: [],
    unavailableElements: [],
    onboarded: false,
    appConfigLoaded: false,
    billingAppConfig: null,
    costCategories: [],
    costCategoriesLoading: false,
    costCategoriesError: null,
    loading: false,
    error: false,
  },

  mutations: {
    setDrawer: set('drawer'),
    setMasterElements: set('masterElements'),
    setUnavailableElements: set('unavailableElements'),
    setOnboarded: set('onboarded'),
    setAppConfigLoaded: set('appConfigLoaded'),
    setBillingAppConfig: set('billingAppConfig'),
    setCostCategories: set('costCategories'),
    setCostCategoriesLoading: set('costCategoriesLoading'),
    setCostCategoriesError: set('costCategoriesError'),
    setLoading: set('loading'),
    setError: set('error'),
  },

  actions: {
    getOnboardingState: async ({ commit, dispatch }) => {
      commit('setLoading', true);
      commit('setError', false);

      try {
        await dispatch('loadBillingAppConfig');

        const availability = await Promise.all(
          BILLING_ELEMENT_NAMES.map(async (elementName) => {
            const element = await dispatch('getBillingElement', elementName);

            return {
              elementName,
              element,
            };
          }),
        );

        const unavailableElements = availability
          .filter(({ element }) => !element)
          .map(({ elementName }) => elementName);

        commit('setUnavailableElements', unavailableElements);

        const synced = await dispatch('syncBillingElementTags', availability);

        if (!unavailableElements.length) {
          commit('setOnboarded', synced);
          return synced;
        }

        commit(
          'setMasterElements',
          BILLING_ELEMENTS.filter(({ element }) => (
            unavailableElements.includes(element.elementName)
          )),
        );

        const created = await dispatch('createBillingElements');

        commit('setOnboarded', created && synced);

        return created && synced;
      } catch (error) {
        console.error('[BillingApp] Onboarding failed:', error);
        commit('setError', true);
        commit('setOnboarded', false);

        return false;
      } finally {
        commit('setLoading', false);
      }
    },

    getBillingElement: async ({ dispatch }, elementName) => {
      const element = await dispatch(
        'element/getElement',
        elementName,
        { root: true },
      );

      return element;
    },

    loadBillingAppConfig: async ({ commit }) => {
      if (!BillingAppConfigService?.getActiveConfig) {
        commit('setAppConfigLoaded', true);
        return null;
      }

      try {
        const billingConfig = await BillingAppConfigService.getActiveConfig();

        if (billingConfig) {
          appConfig.setBillingAppConfig(billingConfig);
          commit('setBillingAppConfig', billingConfig);
        }

        commit('setAppConfigLoaded', true);
        return billingConfig;
      } catch (error) {
        console.warn('[BillingApp] Failed loading billing app config:', error);
        commit('setAppConfigLoaded', false);
        return null;
      }
    },

    saveBillingAppConfig: async ({ commit }, config) => {
      const savedConfig = await BillingAppConfigService.saveConfig(config);

      if (savedConfig) {
        appConfig.setBillingAppConfig(savedConfig);
        commit('setBillingAppConfig', savedConfig);
      }

      return savedConfig;
    },

    fetchCostCategories: async ({ commit }) => {
      commit('setCostCategoriesLoading', true);
      commit('setCostCategoriesError', null);

      try {
        const categories = await CostCategoriesService.getAllCategories();
        commit('setCostCategories', categories || []);
        return categories || [];
      } catch (error) {
        commit('setCostCategoriesError', error.message);
        commit('setCostCategories', []);
        throw error;
      } finally {
        commit('setCostCategoriesLoading', false);
      }
    },

    syncBillingElementTags: async ({ dispatch }, availability) => {
      const payload = availability.reduce((updates, { elementName, element }) => {
        const definition = BILLING_ELEMENTS.find(({ element: billingElement }) => (
          billingElement.elementName === elementName
        ));

        if (!definition) {
          return updates;
        }

        return [
          ...updates,
          ...getTagSyncPayload(element, definition),
        ];
      }, []);

      if (!payload.length) {
        return true;
      }

      const updated = await dispatch(
        'element/updateTagById',
        { payload },
        { root: true },
      );

      if (!updated) {
        console.error('[BillingApp] Failed syncing billing tag metadata', payload);
      }

      return updated;
    },

    createBillingElements: async ({ state, dispatch, commit }) => {
      const createElementPromises = state.masterElements.map(async (definition) => {
        const payload = {
          element: definition.element,
          tags: definition.tags,
        };

        try {
          const response = await dispatch(
            'element/createElementAndTags',
            payload,
            { root: true },
          );

          return Boolean(response);
        } catch (error) {
          console.error(
            `[BillingApp] Failed creating element ${definition.element.elementName}`,
            error,
          );

          return false;
        }
      });

      const results = await Promise.all(createElementPromises);
      const success = results.every(Boolean);

      commit('setError', !success);

      return success;
    },
  },

  getters: {
    billingAppConfig: (moduleState) => moduleState.billingAppConfig,
    allCostCategories: (moduleState) => moduleState.costCategories,
    activeCostCategories: (moduleState) => moduleState.costCategories
      .filter((category) => category.is_active),
    costCategoriesLoading: (moduleState) => moduleState.costCategoriesLoading,
    costCategoriesError: (moduleState) => moduleState.costCategoriesError,
  },
});
